// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — area over a threshold, through time (research tier, statistics S7;
 * `docs/AYNI_XR_RESEARCH_TIER_PLAN.md`).
 *
 * The area of a region where a field is above (or below) a value, frame by
 * frame: how much of the ocean is in a marine heatwave, how much of a
 * country is under a smoke plume. Per frame it gives the area in km², the
 * share of the region's area that has data, and the cells counted.
 *
 * What it computes, and how:
 *
 * - **Cells are the dataset's own grid cells**, the texels of the frame on
 *   the globe, over the same window the region statistics use
 *   (`datasetStats.windowForBounds`: whole cells whose centres fall in the
 *   region). Nothing is regridded or reprojected; a release's map is read
 *   without its calibration strip (`MapRenderer.analysisFrame`).
 * - **Each cell's area is exact on the sphere**: a cell between latitudes
 *   φ₁ and φ₂ and Δλ of longitude covers R² · Δλ · (sin φ₂ − sin φ₁)
 *   (the area of a spherical zone, Archimedes; see Snyder 1987, *Map
 *   Projections — A Working Manual*, USGS PP 1395, p. 16). R is the
 *   authalic radius of the WGS84 ellipsoid, 6371.0072 km: the sphere with
 *   the ellipsoid's surface area (Moritz 2000, *Geodetic Reference System
 *   1980*, J. Geodesy 74:128–133, R₂ = 6 371 007.181 m), so the globe's
 *   cells sum to the Earth's 510.07 million km². Against the ellipsoid
 *   itself a single cell is about 0.45 % smaller near the equator and up to
 *   0.9 % larger near the poles; totals over wide regions agree.
 * - **The comparison is made on decoded values**, never on codes: each
 *   cell's 8-bit code is turned into its value (the release's own decoding
 *   for log and stepped scales), then compared. "Above" is strictly greater
 *   than the threshold, "below" strictly less.
 * - **The 8-bit step is reported, not hidden.** A cell whose value is
 *   within one quantisation step of the threshold could be on either side
 *   of it in the source data. Their area is reported as a range: the area
 *   over the threshold is between (area − those over) and (area + those not
 *   over). With a frame held as the baseline both frames carry a step, so
 *   the band is the two steps added; in σ units it is the step ÷ the
 *   cell's σ.
 * - **No data is excluded and counted.** A cell without data (or, for a
 *   change, without the baseline's value, or for σ without a σ) is not in
 *   the area with data; the share is of the area with data, and the cells
 *   without are reported beside it.
 * - **Against a baseline**: the change is the value minus the held frame's
 *   value in the same cell, or minus the period's mean for the frame's own
 *   group (`climatology.ts`: the calendar month, or the month and UTC hours
 *   for data a few hours apart, taken from the frame's index on the axis by
 *   `frameOf`); the standardised change is that difference ÷ the group's σ
 *   (the residual SD about the period's linear trend, after Hawkins & Sutton
 *   2012). The climatology's mean and σ are decoded from its 16-bit codes
 *   (`decodeMean`, `decodeSigma`). When the climatology was thinned (one
 *   texel in k each way), each cell is read at the texel its climatology
 *   cell was read at (`sampledTexel`), as the globe and the S2 statistics
 *   do, while keeping its own area. A
 *   threshold of "σ change above 2" is the marine-heatwave style of
 *   definition, though Hobday et al. (2016, Prog. Oceanogr. 141:227–238)
 *   use the 90th percentile of a daily climatology and a 5-day minimum
 *   duration, which this does not compute.
 *
 * The series keeps one result per frame of the dataset's time axis (a
 * stream's `.dsa`), keyed by the frame's index, so a replayed frame
 * overwrites its point and nothing is appended twice. Frames the publisher
 * filled, interpolated, estimated or lists as missing (the `.dsa`
 * availability) are not measured, as S2 skips them: they are not
 * observations.
 *
 * Pure: no DOM.
 */

import type { DatasetOverlayOptions } from '../../types'
import { lonSpanDegrees, texelUvToLatLon } from '../datasetProbe'
import { inWindowMask, type TexelWindow } from '../datasetStats'
import { createSeries, type MeasurementSeries } from '../measure/series'
import { climateFrameTimeMs, decodeMean, decodeSigma, sampledTexel, type ClimateAxis, type Climatology } from './climatology'

/**
 * Frame i's stamp on the axis, epoch ms: the time it is labelled with. On a
 * monthly or yearly stream that is the calendar's month or year from the
 * first stamp, not `startMs + i × cadence` (`frameCalendar.ts`).
 */
export function frameStampMs(axis: ClimateAxis, frame: number): number {
  return climateFrameTimeMs(axis, frame)
}

/**
 * The authalic radius of an ellipsoid, km: the radius of the sphere with the
 * same surface area, from S = 2πa²(1 + (1 − e²)/e · artanh e).
 */
export function authalicRadiusKm(aKm: number, flattening: number): number {
  const e2 = flattening * (2 - flattening)
  const e = Math.sqrt(e2)
  const area = 2 * Math.PI * aKm * aKm * (1 + ((1 - e2) / e) * Math.atanh(e))
  return Math.sqrt(area / (4 * Math.PI))
}

/** WGS84's semi-major axis (km) and flattening (NIMA TR8350.2). */
export const WGS84_A_KM = 6378.137
export const WGS84_F = 1 / 298.257223563
/** The WGS84 authalic radius: 6371.0072 km (6 371 007.181 m; Moritz 2000 gives the same for GRS80). */
export const AUTHALIC_RADIUS_KM = authalicRadiusKm(WGS84_A_KM, WGS84_F)

/**
 * The area, km², of a cell between two latitudes (degrees, either order)
 * spanning `dLonDeg` degrees of longitude: R² · Δλ · |sin φ₂ − sin φ₁|.
 */
export function bandAreaKm2(latA: number, latB: number, dLonDeg: number, radiusKm: number = AUTHALIC_RADIUS_KM): number {
  const rad = Math.PI / 180
  return radiusKm * radiusKm * Math.abs(dLonDeg) * rad * Math.abs(Math.sin(latB * rad) - Math.sin(latA * rad))
}

/**
 * The area of one cell in each row of a frame, km², on the dataset's own
 * grid: its rows' edges from the grid's latitudes (a regional box or the
 * globe, flipped or not), its width from the grid's span of longitude.
 * Row 0 is the image's top row, as in the frame.
 */
export function cellRowAreasKm2(width: number, height: number, options?: DatasetOverlayOptions): Float64Array {
  const areas = new Float64Array(Math.max(0, height))
  if (width <= 0 || height <= 0) return areas
  const dLon = lonSpanDegrees(options) / width
  for (let y = 0; y < height; y++) {
    const top = texelUvToLatLon({ u: 0.5, v: y / height }, options).lat
    const bottom = texelUvToLatLon({ u: 0.5, v: (y + 1) / height }, options).lat
    areas[y] = bandAreaKm2(top, bottom, dLon)
  }
  return areas
}

/**
 * One quantisation step at each code: the wider of the gaps to the
 * neighbouring codes' values (so a log scale's uneven steps are its own);
 * NaN for a code without data.
 */
export function codeSteps(values: ArrayLike<number>): Float64Array {
  // The distinct finite values in order, and each one's wider gap to its neighbours.
  const finite: number[] = []
  for (let c = 0; c < values.length; c++) if (Number.isFinite(values[c])) finite.push(values[c])
  finite.sort((a, b) => a - b)
  const uniq = finite.filter((v, i) => i === 0 || v !== finite[i - 1])
  const gap = new Map<number, number>()
  for (let i = 0; i < uniq.length; i++) {
    const below = i > 0 ? uniq[i] - uniq[i - 1] : 0
    const above = i < uniq.length - 1 ? uniq[i + 1] - uniq[i] : 0
    gap.set(uniq[i], Math.max(below, above))
  }
  const out = new Float64Array(values.length).fill(NaN)
  for (let c = 0; c < values.length; c++) {
    const g = Number.isFinite(values[c]) ? gap.get(values[c]) : undefined
    if (g !== undefined && g > 0) out[c] = g
  }
  return out
}

/** What is compared with the threshold: the value, its change from a baseline, or that change in σ. */
export type ThresholdMode = 'value' | 'difference' | 'sigma'
export type Comparator = 'above' | 'below'

export interface ThresholdRule {
  readonly mode: ThresholdMode
  readonly comparator: Comparator
  /** In the data's units, or σ for `sigma`. */
  readonly threshold: number
}

/** What a change is measured from: a held frame on the same grid, or a period's climatology for the frame's group (`frameOf`). */
export type ThresholdReference =
  | { readonly kind: 'frame'; readonly codes: Uint8Array; readonly values: ArrayLike<number>; readonly steps: ArrayLike<number> }
  | { readonly kind: 'climatology'; readonly clim: Climatology; readonly group: number }

/** One frame as the measurement reads it. */
export interface ThresholdFrame {
  readonly codes: Uint8Array
  readonly width: number
  readonly height: number
  /** Each code's value, NaN for no data (`changeMap.valueTable`). */
  readonly values: ArrayLike<number>
  /** Each code's step ({@link codeSteps}). */
  readonly steps: ArrayLike<number>
  /** {@link cellRowAreasKm2} for this grid. */
  readonly rowAreas: Float64Array
  /** The region's cells; undefined for the whole frame. */
  readonly window?: TexelWindow
}

export interface ThresholdResult {
  /** Area of the cells over the threshold, km². */
  readonly areaKm2: number
  /** The same, allowing for the 8-bit step: at least, and at most. */
  readonly areaLowKm2: number
  readonly areaHighKm2: number
  /** Area of the cells that could be compared (with data, and a reference where one is needed), km². */
  readonly areaWithDataKm2: number
  /** Area of every cell of the region, km². */
  readonly regionAreaKm2: number
  /** areaKm2 as a share of areaWithDataKm2, 0–100; null without data. */
  readonly percent: number | null
  readonly cellsOver: number
  readonly cellsWithData: number
  readonly cellsNoData: number
  readonly cellsExamined: number
  /** Cells within one step of the threshold (either side). */
  readonly cellsAmbiguous: number
}

/** Whether `x` is over the threshold. */
function over(x: number, rule: ThresholdRule): boolean {
  return rule.comparator === 'above' ? x > rule.threshold : x < rule.threshold
}

/**
 * Measure one frame: the area over the threshold within the window, the
 * area with data, and the cells counted. A reference is needed for a
 * change (and a climatology with σ for σ); without one no cell can be
 * compared.
 */
export function measureThreshold(frame: ThresholdFrame, rule: ThresholdRule, reference: ThresholdReference | null = null): ThresholdResult {
  const { codes, width, height, values, steps, rowAreas } = frame
  const win = frame.window ?? { x0: 0, y0: 0, x1: width, y1: height }
  const x0 = Math.max(0, Math.floor(win.x0))
  const y0 = Math.max(0, Math.floor(win.y0))
  const x1 = Math.min(width, Math.ceil(win.x1))
  const y1 = Math.min(height, Math.ceil(win.y1))
  let area = 0
  let ambOver = 0
  let ambUnder = 0
  let withData = 0
  let region = 0
  let cellsOver = 0
  let cellsWithData = 0
  let cellsExamined = 0
  let cellsAmbiguous = 0
  const needsRef = rule.mode !== 'value'
  const ref = needsRef ? reference : null
  const frameRef = ref?.kind === 'frame' && ref.codes.length === width * height ? ref : null
  const clim = ref?.kind === 'climatology' && ref.clim.width === width && ref.clim.height === height && ref.group >= 0 && ref.group < ref.clim.groups ? ref : null
  const layer = clim ? clim.group * clim.clim.cw * clim.clim.ch : 0
  // Only a change from a thinned climatology reads the frame elsewhere; values and a held frame read every texel.
  const thinned = clim && clim.clim.factor > 1 ? clim.clim : null
  // AYNI: a region's shape inside its rectangle (a country): only its cells are examined.
  const masked = frame.window?.mask ? frame.window : null

  for (let y = y0; y < y1; y++) {
    const a = rowAreas[y] ?? 0
    const row = y * width
    const cy = clim ? Math.min(clim.clim.ch - 1, Math.floor(y / clim.clim.factor)) * clim.clim.cw : 0
    for (let x = x0; x < x1; x++) {
      if (masked && !inWindowMask(masked, x, y)) continue
      cellsExamined++
      region += a
      // A thinned climatology: the frame read at the texel the cell's climatology was read at.
      const at = thinned ? sampledTexel(thinned, x, y) : null
      const code = at ? codes[at.y * width + at.x] : codes[row + x]
      const v = values[code]
      if (!Number.isFinite(v)) continue
      let q: number
      let band: number
      if (rule.mode === 'value') {
        q = v
        band = steps[code]
      } else if (frameRef) {
        if (rule.mode !== 'difference') continue
        const bc = frameRef.codes[row + x]
        const b = frameRef.values[bc]
        if (!Number.isFinite(b)) continue
        q = v - b
        band = steps[code] + frameRef.steps[bc]
      } else if (clim) {
        const k = (layer + cy + Math.min(clim.clim.cw - 1, Math.floor(x / clim.clim.factor))) * 2
        const mean = decodeMean(clim.clim, clim.clim.q[k])
        if (!Number.isFinite(mean)) continue
        if (rule.mode === 'difference') {
          q = v - mean
          band = steps[code]
        } else {
          const sigma = decodeSigma(clim.clim, clim.clim.q[k], clim.clim.q[k + 1])
          if (!(sigma > 0)) continue
          q = (v - mean) / sigma
          band = steps[code] / sigma
        }
      } else {
        continue
      }
      cellsWithData++
      withData += a
      const isOver = over(q, rule)
      if (isOver) {
        cellsOver++
        area += a
      }
      if (band > 0 && Math.abs(q - rule.threshold) < band) {
        cellsAmbiguous++
        if (isOver) ambOver += a
        else ambUnder += a
      }
    }
  }
  return {
    areaKm2: area,
    areaLowKm2: area - ambOver,
    areaHighKm2: area + ambUnder,
    areaWithDataKm2: withData,
    regionAreaKm2: region,
    percent: withData > 0 ? (100 * area) / withData : null,
    cellsOver,
    cellsWithData,
    cellsNoData: cellsExamined - cellsWithData,
    cellsExamined,
    cellsAmbiguous,
  }
}

// ---------------------------------------------------------------- the series

/**
 * One result per frame of the time axis, by index; null where the frame has
 * not been read. AYNI (2026-10-18): generic, so the region through time
 * (`regionSeries.ts`) keeps its frames the same way.
 */
export interface FrameSeries<T> {
  readonly axis: ClimateAxis
  readonly results: (T | null)[]
  /** 1 for a frame the publisher filled, interpolated, estimated or lists as missing: never measured. */
  readonly skip: Uint8Array
  /** Frames that can be measured (the axis less the skipped ones). */
  readonly total: number
  /** Frames read. */
  read: number
  /** The frame read last (the one on screen), or -1. */
  lastFrame: number
}

export type ThresholdSeries = FrameSeries<ThresholdResult>

/** The most frames a series keeps (one small record each). */
export const MAX_SERIES_FRAMES = 200_000

/**
 * An empty series over the axis's frames; null for an axis without at least
 * two frames. `usable(i)` says whether the publisher measured frame i (the
 * `.dsa` availability); the others are never recorded.
 */
export function createThresholdSeries(axis: ClimateAxis, usable?: (frame: number) => boolean): ThresholdSeries | null {
  return createFrameSeries<ThresholdResult>(axis, usable)
}

/** An empty series of any per-frame result (see {@link createThresholdSeries}). */
export function createFrameSeries<T>(axis: ClimateAxis, usable?: (frame: number) => boolean): FrameSeries<T> | null {
  const n = Math.floor(axis.frameCount)
  if (!(n >= 2) || n > MAX_SERIES_FRAMES || !(axis.cadenceMs > 0) || !Number.isFinite(axis.startMs)) return null
  const skip = new Uint8Array(n)
  let total = n
  if (usable) {
    for (let i = 0; i < n; i++) {
      if (usable(i)) continue
      skip[i] = 1
      total--
    }
  }
  return { axis, results: new Array<T | null>(n).fill(null), skip, total, read: 0, lastFrame: -1 }
}

/** Frames the publisher filled, interpolated, estimated or lists as missing. */
export function skippedThresholdFrames(series: FrameSeries<unknown>): number {
  return series.results.length - series.total
}

/** Put one frame's result in its place; a frame read again replaces its result. False outside the axis, or for a frame the publisher did not measure. */
export function recordThresholdFrame<T>(series: FrameSeries<T>, frame: number, result: T): boolean {
  if (!Number.isInteger(frame) || frame < 0 || frame >= series.results.length || series.skip[frame]) return false
  if (series.results[frame] === null) series.read++
  series.results[frame] = result
  series.lastFrame = frame
  return true
}

/** The frames not read yet that can be, in order. */
export function missingThresholdFrames(series: FrameSeries<unknown>): number[] {
  const out: number[] = []
  for (let i = 0; i < series.results.length; i++) if (series.results[i] === null && !series.skip[i]) out.push(i)
  return out
}

/** The frame a fraction (0–1) of the time axis points at. */
export function frameAtFraction(series: FrameSeries<unknown>, frac: number): number {
  const last = series.results.length - 1
  return Math.min(last, Math.max(0, Math.round(Math.min(1, Math.max(0, frac)) * last)))
}

/** Points on the graph at most: past this, frames share a point (the CSV keeps every frame). */
export const MAX_CHART_SLOTS = 1024

export type ThresholdMetric = 'area' | 'percent'

/**
 * The series as the pins' graph draws one (`measure/chartGeometry.ts`): one
 * slot per frame up to {@link MAX_CHART_SLOTS}; past that a slot shows the
 * last frame (in time) read into it. A frame without data in the region is
 * a slot read with no value.
 */
export function thresholdChartSeries(series: ThresholdSeries, metric: ThresholdMetric): MeasurementSeries | null {
  const n = series.results.length
  const start = series.axis.startMs
  const end = frameStampMs(series.axis, n - 1)
  const chart = createSeries(start, end, Math.min(n, MAX_CHART_SLOTS))
  if (!chart) return null
  const slots = chart.slots.length
  for (let i = 0; i < n; i++) {
    const r = series.results[i]
    if (!r) continue
    const slot = n <= slots ? i : Math.min(slots - 1, Math.round((i / (n - 1)) * (slots - 1)))
    const value = metric === 'percent' ? r.percent : r.cellsWithData > 0 ? r.areaKm2 : null
    chart.slots[slot] = { sampled: true, value, timeMs: frameStampMs(series.axis, i) }
  }
  return chart
}

// ---------------------------------------------------------------- words and the CSV

/** The rule in words, for the CSV (English, like every CSV row): "value > 30 °C". */
export function describeRule(rule: ThresholdRule, units: string | undefined): string {
  const what = rule.mode === 'value' ? 'value' : rule.mode === 'difference' ? 'change from the baseline' : 'standardised change from the baseline'
  const sign = rule.comparator === 'above' ? '>' : '<'
  const u = rule.mode === 'sigma' ? ' sigma' : units ? ` ${units}` : ''
  return `${what} ${sign} ${rule.threshold}${u}`
}

/** The method, for the CSV and the methods text. */
export const THRESHOLD_METHOD =
  'area over a threshold per frame: each cell of the dataset\'s own grid (no regridding or reprojection) over the region\'s cells (whole cells whose centres fall in the region, as the region statistics), compared on its value (strictly above or below); cell area R^2 * dLon * (sin lat2 - sin lat1) on a sphere of radius 6371.0072 km, the WGS84 authalic radius (Moritz 2000), so a cell differs from the ellipsoid by -0.45 % (equator) to +0.9 % (poles); cells without data, or without the baseline where a change is compared, are excluded and counted; area_low/area_high allow for cells within one quantisation step of the threshold' // i18n-exempt: CSV row, English like every CSV row

type Cell = string | number | null | undefined

function cell(value: Cell): string {
  if (value === null || value === undefined) return ''
  const s = String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function csvRows(list: readonly Cell[][]): string {
  return list.map((r) => r.map(cell).join(',')).join('\r\n')
}

export interface ThresholdCsvContext {
  readonly datasetTitle: string | null
  readonly rule: ThresholdRule
  readonly units?: string
  readonly sourceUnits?: string
  /** The region as the panel names it, and its box when it is one. */
  readonly region: string
  readonly regionBounds?: { n: number; s: number; w: number; e: number } | null
  readonly gridWidth: number
  readonly gridHeight: number
  /** One step of the data; null when it is not uniform (log or stepped scales). */
  readonly step: number | null
  /** What the change is measured from, when it is one. */
  readonly baseline?: { readonly label: string; readonly method?: string } | null
  /** The provenance rows every AYNI CSV carries (`research/citation.ts`), without one frame's time. */
  readonly provenance?: readonly (readonly Cell[])[] | null
  /** AYNI: a frame's time at the axis's precision ("1991", "1991-01"; `research/frameTime.ts`); null or absent: the ISO instant. */
  readonly formatTime?: ((ms: number) => string | null) | null
}

/** The series as CSV: what was measured and how, then one row per frame read, in time order. */
export function buildThresholdCsvText(series: ThresholdSeries, ctx: ThresholdCsvContext): string {
  const first = series.results.find((r) => r !== null) ?? null
  const b = ctx.regionBounds
  const head: Cell[][] = [
    ['dataset', ctx.datasetTitle ?? ''],
    ['analysis', 'area over a threshold'],
    ['threshold', describeRule(ctx.rule, ctx.units)],
    ['comparison', ctx.rule.comparator === 'above' ? 'strictly above' : 'strictly below'],
    ['threshold_value', ctx.rule.threshold],
    ['threshold_units', ctx.rule.mode === 'sigma' ? 'sigma' : ctx.units ?? ''],
    ['values', ctx.rule.mode === 'value' ? 'dataset values' : ctx.rule.mode === 'difference' ? 'value minus the baseline' : '(value minus the baseline mean) / baseline sigma (Hawkins & Sutton 2012)'],
    ...(ctx.baseline ? [['baseline', ctx.baseline.label] as Cell[]] : []),
    ...(ctx.baseline?.method ? [['baseline_method', ctx.baseline.method] as Cell[]] : []),
    ['units', ctx.units ?? ''],
    ...(ctx.sourceUnits ? [['source_units', ctx.sourceUnits] as Cell[]] : []),
    ['quantisation_step', ctx.step ?? 'uneven (log or stepped scale): one step at each value'],
    ['region', ctx.region],
    ...(b ? [['region_bounds_nswe', b.n, b.s, b.w, b.e] as Cell[]] : []),
    ['region_cells', first?.cellsExamined ?? ''],
    ['region_area_km2', first?.regionAreaKm2 ?? ''],
    ['grid', `${ctx.gridWidth} x ${ctx.gridHeight} cells (the dataset's own)`],
    ['earth_radius_km', `${AUTHALIC_RADIUS_KM} (WGS84 authalic)`],
    ['frames_in_axis', series.results.length],
    ['frames_not_measured', skippedThresholdFrames(series), "filled, interpolated, estimated or missing in the publisher's .dsa availability; not read"],
    ['frames_read', series.read],
    ['time_axis', 'UTC'],
    ['method', THRESHOLD_METHOD],
    ...(ctx.provenance ?? []).map((r) => [...r]),
    [],
    ['frame_time', 'area_km2', 'percent_of_area_with_data', 'cells_over', 'cells_with_data', 'cells_without_data', 'area_with_data_km2', 'area_low_km2', 'area_high_km2', 'cells_within_one_step', 'frame_index'],
  ]
  const body: Cell[][] = []
  series.results.forEach((r, i) => {
    if (!r) return
    body.push([
      ctx.formatTime?.(frameStampMs(series.axis, i)) ?? new Date(frameStampMs(series.axis, i)).toISOString(),
      r.areaKm2,
      r.percent,
      r.cellsOver,
      r.cellsWithData,
      r.cellsNoData,
      r.areaWithDataKm2,
      r.areaLowKm2,
      r.areaHighKm2,
      r.cellsAmbiguous,
      i,
    ])
  })
  return `${csvRows(head)}\r\n${csvRows(body)}\r\n`
}
